import BugReportIcon from '@mui/icons-material/BugReport'
import DifferenceIcon from '@mui/icons-material/Difference'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import StorageIcon from '@mui/icons-material/Storage'
import TerminalIcon from '@mui/icons-material/Terminal'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { blue, green, orange, purple, red } from '@mui/material/colors'
import { useDeadLetterSubscriptions } from '@src/tools/dead-letter/api/dead-letters-api'
import { useQueueRefreshErrors, useQueueSummary } from '@src/tools/dead-letter/api/queues-api'
import { useReleaseCheckItems } from '@src/tools/release-check/api/release-check-api'
import { hasDiscrepancies } from '@src/tools/release-check/pages/DashboardPage/itemVisibility'
import { useAnonymizerSummary, useCommandSummary, useExceptionsCounts } from '@src/shell/api/dashboard-api'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

const DashboardPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [subscriptions, isFetchingDeadLetters] = useDeadLetterSubscriptions()
  const [errorCount, isFetchingErrors] = useExceptionsCounts()
  const [dbCount, pendingApprovalCount, isFetchingAnonymizer] = useAnonymizerSummary()
  const [disabledTimerCount, isFetchingCommand] = useCommandSummary()
  const [snapshots] = useQueueSummary()
  const [refreshErrors] = useQueueRefreshErrors()
  const [releaseCheckItems, isFetchingReleaseCheck] = useReleaseCheckItems()

  const discrepancyCount = useMemo(
    () => releaseCheckItems.filter((i) => !i.ignored && hasDiscrepancies(i)).length,
    [releaseCheckItems],
  )

  const deadLetterCount = useMemo(
    () => subscriptions.reduce((sum, sub) => sum + sub.eventCount, 0),
    [subscriptions],
  )

  const poisonQueueCount = useMemo(
    () => snapshots.filter((s) => s.isPoison && s.messageCount > 0).length,
    [snapshots],
  )

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Stack sx={{ mx: '15px' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', my: 2 }}>
          {t('dashboard.title')}
        </Typography>

        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={3} sx={{ mt: 1 }}>
          {/* Errors Card */}
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 0,
              flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              border: 1,
              borderColor: 'divider',
            }}
          >
            <CardActionArea onClick={() => navigate('/error-tracker')} sx={{ flexGrow: 1 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: orange[50],
                    }}
                  >
                    <BugReportIcon sx={{ fontSize: 32, color: orange[700] }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      {t('dashboard.newExceptions')}
                    </Typography>
                    {isFetchingErrors ? (
                      <CircularProgress size={28} />
                    ) : (
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {errorCount.toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>

          {/* Dead Letters Card */}
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 0,
              flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              border: 1,
              borderColor: 'divider',
            }}
          >
            <CardActionArea onClick={() => navigate('/dead-letter')} sx={{ flexGrow: 1 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: blue[50],
                    }}
                  >
                    <ErrorOutlineIcon sx={{ fontSize: 32, color: blue[700] }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      {t('dashboard.deadLetterTool')}
                    </Typography>
                    {isFetchingDeadLetters ? (
                      <CircularProgress size={28} />
                    ) : (
                      <Stack>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {deadLetterCount.toLocaleString()}{' '}
                          <Typography component="span" variant="body2" color="text.secondary">
                            {t('dashboard.deadLetters')}
                          </Typography>
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {poisonQueueCount.toLocaleString()}{' '}
                            <Typography component="span" variant="body2" color="text.secondary">
                              {t('dashboard.poisonQueues')}
                            </Typography>
                          </Typography>
                          {refreshErrors.length > 0 && (
                            <Chip size="small" label={`${refreshErrors.length} ${t('dashboard.errors')}`} color="error" sx={{ borderRadius: '12px', fontWeight: 600 }} />
                          )}
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>

          {/* Command Card */}
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 0,
              flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              border: 1,
              borderColor: 'divider',
            }}
          >
            <CardActionArea onClick={() => navigate('/command/disabled')} sx={{ flexGrow: 1 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: green[50],
                    }}
                  >
                    <TerminalIcon sx={{ fontSize: 32, color: green[700] }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      {t('dashboard.commandTool', 'Functions')}
                    </Typography>
                    {isFetchingCommand ? (
                      <CircularProgress size={28} />
                    ) : (
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {disabledTimerCount.toLocaleString()}{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {t('dashboard.disabledTimers', 'disabled timers')}
                        </Typography>
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>

          {/* Anonymizer Card */}
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 0,
              flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              border: 1,
              borderColor: 'divider',
            }}
          >
            <CardActionArea onClick={() => navigate('/anonymizer')} sx={{ flexGrow: 1 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: purple[50],
                    }}
                  >
                    <StorageIcon sx={{ fontSize: 32, color: purple[700] }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      {t('dashboard.anonymizer')}
                    </Typography>
                    {isFetchingAnonymizer ? (
                      <CircularProgress size={28} />
                    ) : (
                      <Stack>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {dbCount.toLocaleString()}{' '}
                          <Typography component="span" variant="body2" color="text.secondary">
                            {t('dashboard.databases')}
                          </Typography>
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {pendingApprovalCount.toLocaleString()}{' '}
                          <Typography component="span" variant="body2" color="text.secondary">
                            {t('dashboard.pendingApproval')}
                          </Typography>
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>

          {/* Release Check Card */}
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 0,
              flexBasis: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              maxWidth: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc((100% - 48px) / 3)' },
              border: 1,
              borderColor: 'divider',
            }}
          >
            <CardActionArea onClick={() => navigate('/release-check')} sx={{ flexGrow: 1 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: red[50],
                    }}
                  >
                    <DifferenceIcon sx={{ fontSize: 32, color: red[700] }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">
                      {t('dashboard.releaseCheckTool', 'Release Check')}
                    </Typography>
                    {isFetchingReleaseCheck ? (
                      <CircularProgress size={28} />
                    ) : (
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {discrepancyCount.toLocaleString()}{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {t('dashboard.discrepancies', 'discrepancies')}
                        </Typography>
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        </Stack>
      </Stack>
    </Box>
  )
}

export default DashboardPage
